"use client";

import { Filters } from "@/types/filters";
import { logger } from "@/utils/logger";
import { filtersToUrl, urlToFilters } from "@/utils/search-url-transform";
import { parseFiltersFromSearchParams } from "@/utils/search/parse-search-params";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useSearchPageInteraction, useSearchPageMapData } from "../providers/search";

export type ViewMode = "map" | "list";
export type SearchMode = "default" | "project";

interface UrlState {
	filters: Partial<Filters>;
	mode: SearchMode;
	view: ViewMode;
}

interface UrlStateContextType {
	state: UrlState;
	setUrlState: (newState: Partial<UrlState>, options?: { useReplaceState?: boolean }) => void;
	setFilters: (filters: Partial<Filters>, options?: { useReplaceState?: boolean }) => void;
	setMode: (mode: SearchMode) => void;
	setView: (view: ViewMode) => void;
}

export const UrlStateContext = createContext<UrlStateContextType | undefined>(undefined);

export function UrlStateProvider({
	children,
	initialFilters,
}: {
	children: React.ReactNode;
	initialFilters?: Partial<Filters>;
}) {
	const pathname = usePathname();
	const router = useRouter();
	const searchParams = useSearchParams();

	// Store setters for synchronization
	const setStoreView = useSearchPageInteraction(state => state.setView);
	const storeView = useSearchPageInteraction(state => state.view);
	const setStoreMode = useSearchPageMapData(state => state.setMode);
	const storeMode = useSearchPageMapData(state => state.mode);

	// Ref to track updates originated from this hook to prevent circular sync
	const internalUpdateRef = useRef(false);

	// Ref to track pending state changes (to handle rapid successive calls)
	const pendingStateRef = useRef<Partial<UrlState>>({});

	// Parse state from URL - Single Source of Truth
	const urlState = useMemo<UrlState>(() => {
		const slug = pathname?.split("/").pop() || "";
		const params = searchParams || new URLSearchParams();

		// 1. Parse filters
		let filters = {
			...urlToFilters(slug),
			...parseFiltersFromSearchParams(params),
		} as Partial<Filters>;

		// On initial load, merge with initialFilters if URL has no filters
		if (initialFilters && Object.keys(filters).length === 0) {
			filters = initialFilters;
		} else {
			// Only enrich locations if we actually parsed something or if they exist
			// enrich locations from initialFilters if available
			filters.locations =
				filters.locations?.map(location => ({
					...location,
					...(initialFilters?.locations?.find(item => item.name === location.name) || {}),
				})) || [];
		}

		// 2. Parse mode (properties/projects)
		const modeParam = params.get("mode");
		const mode: SearchMode = modeParam === "projects" ? "project" : "default";

		// 3. Parse view (map/list)
		const viewParam = params.get("view");
		const view: ViewMode = viewParam === "list" ? "list" : "map";

		return { filters, mode, view };
	}, [pathname, searchParams, initialFilters]);

	// Atomic update function
	const setUrlState = useCallback(
		(newState: Partial<UrlState>, options: { useReplaceState?: boolean } = {}) => {
			const { useReplaceState = false } = options;

			// Merge with pending state to handle rapid successive calls
			pendingStateRef.current = { ...pendingStateRef.current, ...newState };

			// Merge with URL state, using pending state as intermediate source
			const currentState = { ...urlState, ...pendingStateRef.current };
			const mergedFilters = newState.filters !== undefined ? newState.filters : currentState.filters;
			const mergedMode = newState.mode !== undefined ? newState.mode : currentState.mode;
			const mergedView = newState.view !== undefined ? newState.view : currentState.view;

			// Build Path
			const { bounds, selectedLocationId, coordinates, zoom, ...pathFilters } = mergedFilters;
			const newSlug = filtersToUrl(pathFilters);
			const segments = pathname?.split("/").filter(Boolean) || [];
			const citySegment = segments[0] || "bengaluru";
			const newPath = newSlug ? `/${citySegment}/${newSlug}` : `/${citySegment}`;

			// Build Search Params
			const newSearchParams = new URLSearchParams(window.location.search);

			// View
			if (mergedView === "map") {
				newSearchParams.delete("view");
			} else {
				newSearchParams.set("view", "list");
			}

			// Mode
			if (mergedMode === "project") {
				newSearchParams.set("mode", "projects");
			} else {
				newSearchParams.delete("mode");
			}

			// Bounds
			if (bounds && bounds.length > 0) {
				const boundsObj = bounds[0];
				newSearchParams.set("bounds", `${boundsObj.north}-${boundsObj.east}-${boundsObj.south}-${boundsObj.west}`);
			} else {
				newSearchParams.delete("bounds");
			}

			// Location ID
			if (selectedLocationId) {
				newSearchParams.set("selectedLocationId", selectedLocationId);
			} else {
				newSearchParams.delete("selectedLocationId");
			}

			// Coordinates
			if (coordinates) {
				newSearchParams.set("center", `${coordinates.lat}-${coordinates.lng}`);
			} else {
				newSearchParams.delete("center");
			}

			// Zoom
			if (zoom) {
				newSearchParams.set("zoom", zoom.toString());
			} else {
				newSearchParams.delete("zoom");
			}

			const finalUrl = newSearchParams.toString() ? `${newPath}?${newSearchParams.toString()}` : newPath;

			internalUpdateRef.current = true;

			// Determine if mode or view changed (need router.push for these)
			const modeOrViewChanged = newState.mode !== undefined || newState.view !== undefined;

			logger.debug("[useUrlState] setUrlState updating URL", {
				finalUrl,
				useReplaceState,
				modeOrViewChanged,
			});

			// IMPORTANT: Use different navigation methods based on what changed
			// - Mode/View changes: Use router.push to trigger Next.js hooks (useSearchParams updates)
			// - Filter changes: Use window.history.pushState to avoid remounting (smoother UX)
			if (modeOrViewChanged) {
				// Mode or view changed - use router to trigger Next.js hooks
				if (useReplaceState) {
					router.replace(finalUrl, { scroll: false });
				} else {
					router.push(finalUrl, { scroll: false });
				}
			} else {
				// Only filters changed - use pushState to avoid remounting
				if (useReplaceState) {
					window.history.replaceState(null, "", finalUrl);
				} else {
					window.history.pushState(null, "", finalUrl);
				}
			}

			setTimeout(() => {
				internalUpdateRef.current = false;
				// Clear pending state after URL has updated
				pendingStateRef.current = {};
			}, 100);
		},
		[pathname, router, urlState],
	);

	// Wrappers for backward compatibility and convenience
	const setFilters = useCallback(
		(filters: Partial<Filters>, options?: { useReplaceState?: boolean }) => {
			setUrlState({ filters }, options);
		},
		[setUrlState],
	);

	const setMode = useCallback(
		(mode: SearchMode) => {
			setUrlState({ mode });
		},
		[setUrlState],
	);

	const setView = useCallback(
		(view: ViewMode) => {
			setUrlState({ view });
		},
		[setUrlState],
	);

	// Sync URL -> Store
	useEffect(() => {
		if (urlState.view !== storeView) {
			setStoreView(urlState.view);
		}
		if (urlState.mode !== storeMode) {
			setStoreMode(urlState.mode);
		}
	}, [urlState.view, urlState.mode, setStoreView, setStoreMode, storeView, storeMode]);

	return (
		<UrlStateContext.Provider value={{ state: urlState, setUrlState, setFilters, setMode, setView }}>
			{children}
		</UrlStateContext.Provider>
	);
}

export function useUrlState() {
	const context = useContext(UrlStateContext);
	if (context === undefined) {
		throw new Error("useUrlState must be used within a UrlStateProvider");
	}
	return context;
}
